import { useEffect, useState } from 'react';
import { api, ErroApi, type IndicacoesView } from '../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, mascaraCelular,
} from '../componentes/ui';

const moeda = (v: number | string) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataCurta = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');
const mesCurto = (c: string) => {
  const [a, m] = c.split('-');
  return `${['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][Number(m) - 1]}/${a}`;
};

const STATUS_INDICACAO: Record<string, string> = { pendente: 'neutro', convertida: 'ok', cancelada: 'inativo' };
const STATUS_SAQUE: Record<string, string> = { solicitado: 'neutro', pago: 'ok', recusado: 'inativo' };

/**
 * Indique e ganhe: quem atende por um escritório indica um cliente em
 * potencial e, se ele virar cliente de verdade, acumula uma comissão sobre
 * a mensalidade dele todo mês. O crédito é de quem indicou, não do
 * escritório — então um funcionário que indicou continua recebendo mesmo
 * se um dia sair de lá.
 */
export function Indicacoes() {
  const [dados, setDados] = useState<IndicacoesView | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [nova, setNova] = useState(false);

  async function carregar() {
    try {
      setDados(await api.indicacoes());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar suas indicações.');
    }
  }
  useEffect(() => { void carregar(); }, []);

  async function sacar() {
    setErro('');
    setOcupado(true);
    try {
      await api.solicitarSaque();
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui pedir o saque.');
    } finally {
      setOcupado(false);
    }
  }

  const saquePendente = dados?.saques.some((s) => s.status === 'solicitado') ?? false;
  const podeSacar = Boolean(dados && dados.saldo >= dados.saqueMinimo && !saquePendente);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Indique e ganhe</h1>
          <p>
            Indicou um cliente novo e ele virou cliente do Conti9? Você acumula 3% da mensalidade dele
            todo mês, e pode sacar a partir de {dados ? moeda(dados.saqueMinimo) : '—'}.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Nova indicação</button>
      </header>

      <Erro>{erro}</Erro>

      {!dados ? <Carregando /> : (
        <>
          <section className="cartao-saldo" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 20px', marginBottom: 24, border: '1px solid var(--border-strong)', borderRadius: 12,
          }}>
            <div>
              <div className="campo-ajuda" style={{ marginBottom: 4 }}>Saldo acumulado</div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{moeda(dados.saldo)}</div>
            </div>
            <button className="btn btn-primary" disabled={!podeSacar || ocupado} onClick={sacar}>
              {ocupado ? <span className="spinner" /> : saquePendente ? 'Saque em análise' : 'Sacar'}
            </button>
          </section>

          <section>
            <h2>Minhas indicações</h2>
            {dados.indicacoes.length === 0 ? (
              <Vazio titulo="Nenhuma indicação ainda" descricao="Indique um cliente em potencial e acompanhe aqui." />
            ) : (
              <Tabela
                itens={dados.indicacoes}
                chaveDe={(i) => i.id}
                colunas={[
                  { rotulo: 'Indicado', principal: true, celula: (i) => <strong>{i.nomeProspecto}</strong> },
                  { rotulo: 'Virou', fraco: true, celula: (i) => i.empresa?.razaoSocial ?? '—' },
                  { rotulo: 'Data', fraco: true, celula: (i) => dataCurta(i.criadoEm) },
                  { rotulo: 'Status', celula: (i) => <Selo tipo={STATUS_INDICACAO[i.status]} texto={i.status} /> },
                ]}
              />
            )}
          </section>

          <section style={{ marginTop: 28 }}>
            <h2>Extrato de comissão</h2>
            {dados.lancamentos.length === 0 ? (
              <Vazio titulo="Nenhum lançamento ainda" descricao="Aparece aqui assim que uma indicação convertida fechar o primeiro mês." />
            ) : (
              <Tabela
                itens={dados.lancamentos}
                chaveDe={(l) => l.id}
                colunas={[
                  { rotulo: 'Mês', principal: true, celula: (l) => mesCurto(l.mesReferencia) },
                  { rotulo: 'Cliente', fraco: true, celula: (l) => l.empresa },
                  { rotulo: 'Percentual', numero: true, fraco: true, celula: (l) => `${Number(l.percentual).toFixed(0)}%` },
                  { rotulo: 'Valor', numero: true, celula: (l) => moeda(l.valor) },
                ]}
              />
            )}
          </section>

          <section style={{ marginTop: 28 }}>
            <h2>Saques</h2>
            {dados.saques.length === 0 ? (
              <Vazio titulo="Nenhum saque pedido ainda" />
            ) : (
              <Tabela
                itens={dados.saques}
                chaveDe={(s) => s.id}
                colunas={[
                  { rotulo: 'Pedido em', principal: true, celula: (s) => dataCurta(s.criadoEm) },
                  { rotulo: 'Valor', numero: true, celula: (s) => moeda(s.valor) },
                  { rotulo: 'Status', celula: (s) => <Selo tipo={STATUS_SAQUE[s.status]} texto={s.status} /> },
                ]}
              />
            )}
          </section>
        </>
      )}

      {nova && (
        <NovaIndicacao
          onFechar={() => setNova(false)}
          onCriada={() => { setNova(false); void carregar(); }}
        />
      )}
    </div>
  );
}

function NovaIndicacao({ onFechar, onCriada }: { onFechar: () => void; onCriada: () => void }) {
  const [nome, setNome] = useState('');
  const [celular, setCelular] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      await api.criarIndicacao({
        nomeProspecto: nome,
        celularProspecto: celular || undefined,
        observacao: observacao || undefined,
      });
      onCriada();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo="Nova indicação"
      descricao="Conte quem você está indicando. A gente entra em contato — se virar cliente, a comissão fica registrada aqui."
      onFechar={onFechar}
    >
      <Erro>{erro}</Erro>
      <Campo rotulo="Nome do indicado">
        <input className="input" value={nome} onChange={(e) => setNome(e.target.value)}
          placeholder="Nome da pessoa ou da empresa" autoFocus />
      </Campo>
      <Campo rotulo="Celular" ajuda="Opcional — ajuda a gente a entrar em contato mais rápido.">
        <input className="input" value={celular} onChange={(e) => setCelular(mascaraCelular(e.target.value))}
          placeholder="(00) 00000-0000" inputMode="numeric" />
      </Campo>
      <Campo rotulo="Observação" ajuda="Opcional.">
        <input className="input" value={observacao} onChange={(e) => setObservacao(e.target.value)}
          placeholder="Algum contexto que ajude" />
      </Campo>

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || nome.trim().length < 2}>
          {salvando ? <span className="spinner" /> : 'Indicar'}
        </button>
      </div>
    </Modal>
  );
}
